import React, { useState } from 'react';
import { translate } from 'translations/i18n';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import CCModal from 'components/shared/DesignSystem/CCModal/CCModal';
import Steven from 'javascripts/helpers/steven';
import FeatureList from '../shared/FeatureList';
import { Button, Notice } from '@companycam/slab-web';
import Text from 'components/shared/DesignSystem/SlabTransferPortal/Text';
import useSubscriptionPlanUpdate from '../billingCycle/useSubscriptionPlanUpdate';
import useDetailedBillingQuery from '../useDetailedBillingQuery';
import { useTrialEndDays } from './hooks';
import { formatDistanceToNow } from 'date-fns';

// #region styled-components
const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: var(--cc_size_spacing_l) var(--cc_size_spacing_m);
  justify-content: space-between;
`;

const ConfirmButton = styled(Button)`
  background: var(--cc_color_button_background_destroy);
  color: var(--cc_color_button_text_primary);
  margin-left: var(--cc_size_spacing_s);
  &:hover {
    background: var(--cc_color_button_background_destroy_darken_15);
  }
`;

const FeatureListContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: var(--cc_size_spacing_xl);
  margin-top: var(--cc_size_spacing_l);
`;
// #endregion

const CancelPremiumTrialModal = ({ onRequestClose }) => {
  const [error, setError] = useState('');
  const { subscription } = useDetailedBillingQuery();
  const subscriptionId = subscription?.previousSubscriptionOption?.id;
  const { subscriptionPlanUpdate, loading } = useSubscriptionPlanUpdate({
    onComplete: onRequestClose,
    customToast: {
      title: translate('billing.v2.premiumTrial.canceledPremiumTrialMessage'),
    },
    onError: () => {
      setError(translate('billing.v2.premiumTrial.cancelPremiumTrialError'));
    },
  });
  const { isoSampleEnd } = useTrialEndDays();

  const confirmCancelTrial = () => {
    Steven.elementClicked({
      id: 'cancel_premium_trial_modal_button',
      content: 'Yes, Cancel My Upgrade',
      type: 'button',
      action: 'cancel_premium_trial',
    });
    subscriptionPlanUpdate({ id: subscriptionId });
  };

  return (
    <CCModal
      isOpen
      showCloseButton={false}
      onRequestClose={onRequestClose}
      className="modal-content-wrap is-padless"
      primaryAction={{
        component: (
          <ConfirmButton
            loading={loading}
            data-testid="modal__cancel-trial-premium"
            onClick={confirmCancelTrial}
          >
            {translate('billing.v2.premiumTrial.cancelPremiumTrialLabel')}
          </ConfirmButton>
        ),
      }}
      secondaryAction={{
        label: translate(
          'billing.v2.premiumTrial.declineCancelPremiumTrialLabel',
        ),
        onClick: onRequestClose,
        disabled: loading,
        'data-testid': 'modal__trial-premium-decline',
      }}
    >
      <ContentContainer>
        <Text size="xl">
          {translate('billing.v2.premiumTrial.cancelPremiumTrialModalTitle')}
        </Text>
        <FeatureListContainer>
          <FeatureList
            features={[
              translate('billing.v2.premiumTrial.cancelPremiumFeatureOne'),
              translate('billing.v2.premiumTrial.cancelPremiumFeatureTwo'),
              translate('billing.v2.premiumTrial.cancelPremiumFeatureThree'),
            ]}
          />
          <FeatureList
            features={[
              translate('billing.v2.premiumTrial.cancelPremiumFeatureFour'),
              translate('billing.v2.premiumTrial.cancelPremiumFeatureFive'),
              translate('billing.v2.premiumTrial.cancelPremiumFeatureSix'),
            ]}
          />
        </FeatureListContainer>
        <Notice ccMargin="l none none" color="info">
          {translate('billing.v2.premiumTrial.premiumTrialDaysLeft', {
            time: formatDistanceToNow(isoSampleEnd),
          })}
        </Notice>
        {error ? (
          <Notice ccMargin="l none none" color="destroy">
            {translate('billing.v2.premiumTrial.cancelPremiumTrialError')}
          </Notice>
        ) : null}
      </ContentContainer>
    </CCModal>
  );
};

CancelPremiumTrialModal.propTypes = {
  onRequestClose: PropTypes.func,
};

export default CancelPremiumTrialModal;
